import React from 'react';
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, Music2, MessageSquare } from 'lucide-react';
import { companyInfo } from '../data/mock';

export const Contact = () => {
  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Contactez-Nous
          </h2>
          <p className="text-xl text-gray-600">
            Nous sommes là pour vous aider. Visitez-nous ou contactez-nous !
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                {companyInfo.name}
              </h3>
              <p className="text-lg text-gray-600 mb-4">
                Votre expert réparation smartphones, tablettes &amp; PC à <strong>Villeurbanne</strong> depuis 10 ans.
                Rapport qualité-prix imbattable, intervention rapide, diagnostic gratuit.
              </p>
              <p className="text-sm text-gray-500 mb-8">
                Nous intervenons à Villeurbanne (69100), <strong>Lyon</strong>, <strong>Bron</strong>, <strong>Vaulx-en-Velin</strong>,
                Caluire-et-Cuire, Décines, Meyzieu, Rillieux-la-Pape, Saint-Priest et Vénissieux.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-4">
              {/* Address */}
              <div className="flex items-start space-x-4 bg-white border-2 border-gray-200 rounded-2xl p-6 hover:border-blue-500 transition-colors">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-blue-600" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Adresse</h4>
                  <p className="text-gray-600">{companyInfo.address}</p>
                  <a
                    href={companyInfo.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium mt-2 inline-block"
                  >
                    Voir sur Google Maps →
                  </a>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start space-x-4 bg-white border-2 border-gray-200 rounded-2xl p-6 hover:border-blue-500 transition-colors">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="text-green-600" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Téléphone</h4>
                  <a
                    href={`tel:${companyInfo.phone}`}
                    className="text-gray-600 hover:text-blue-600 block"
                  >
                    {companyInfo.phone}
                  </a>
                  <a
                    href={`tel:${companyInfo.phone2}`}
                    className="text-gray-600 hover:text-blue-600 block"
                  >
                    {companyInfo.phone2}
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start space-x-4 bg-white border-2 border-gray-200 rounded-2xl p-6 hover:border-blue-500 transition-colors">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="text-purple-600" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="text-gray-600 hover:text-blue-600"
                  >
                    {companyInfo.email}
                  </a>
                </div>
              </div>

              {/* Hours */}
              <div className="flex items-start space-x-4 bg-white border-2 border-gray-200 rounded-2xl p-6 hover:border-blue-500 transition-colors">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="text-orange-600" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Heures d'ouverture</h4>
                  <p className="text-gray-600">{companyInfo.hours.allDays}</p>
                  <p className="text-sm text-orange-600 font-medium mt-1">Ouvert NON-STOP</p>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100 rounded-2xl p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Suivez-nous sur les réseaux</h4>
              <div className="flex flex-wrap gap-3">
                <a
                  href={companyInfo.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white hover:bg-blue-700 transition-all transform hover:scale-110 shadow-md"
                  aria-label="Facebook"
                >
                  <Facebook size={24} />
                </a>
                <a
                  href={companyInfo.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center text-white hover:from-purple-700 hover:to-pink-700 transition-all transform hover:scale-110 shadow-md"
                  aria-label="Instagram"
                >
                  <Instagram size={24} />
                </a>
                <a
                  href={companyInfo.social.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-black rounded-xl flex items-center justify-center text-white hover:bg-gray-800 transition-all transform hover:scale-110 shadow-md"
                  aria-label="TikTok"
                >
                  <Music2 size={24} />
                </a>
              </div>
            </div>
          </div>

          {/* Google Map - ADRESSE CORRIGÉE 6 Rue Paul Verlaine */}
          <div className="h-[600px] rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2782.5998891782604!2d4.875649476622846!3d45.76867097108498!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47f4c02ee1ed7953%3A0xc1df26b408b14012!2sK%26O%20Phonelab%20-%20Magasin%20Ind%C3%A9pendant%20-%20R%C3%A9paration%20Smartphones%20-%20Tablettes%20-%20Ordinateurs!5e0!3m2!1sfr!2sfr!4v1738363847654!5m2!1sfr!2sfr"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="K&O PHONELAB - 6 Rue Paul Verlaine, Villeurbanne"
            ></iframe>
          </div>
        </div>

        {/* CTA Banner */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl p-12 text-center text-white shadow-2xl">
          <h3 className="text-3xl font-bold mb-4">
            Réparation Sans Rendez-Vous
          </h3>
          <p className="text-xl text-blue-100 mb-8">
            Venez directement à notre boutique ! Nos techniciens sont prêts à réparer votre appareil.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${companyInfo.phoneFormatted}`}
              className="inline-flex items-center justify-center space-x-2 bg-white text-blue-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              <Phone size={20} />
              <span>Appelez maintenant</span>
            </a>
            <a
              href={companyInfo.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center space-x-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300 transform hover:scale-105"
            >
              <MapPin size={20} />
              <span>Obtenir l'itinéraire</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
